import {MONITOR_TOUR_STEPS,MONITOR_TOUR_VERSION} from './tour/monitorTour';
/**
 * OnboardingTour.jsx — Guided Tour System
 *
 * Scoped Overview tour and the existing module tours.
 * Supports goal-based flows (research / landscape / monitor)
 * Overview starts from an invitation, page Help or Settings without a reload.
 *
 * State:  localStorage  bds_tour_completed_{page}
 *         localStorage  bds_tour_dismissed_{page}
 *         localStorage  bds_user_goal  (set by WelcomeOverlay)
 */
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { createPortal } from 'react-dom';
import { TourSpotlight } from './tour/TourSpotlight';
import { useTourGeometry, isVisibleTourTarget } from './tour/useTourGeometry';
import {OVERVIEW_TOUR_STEPS,readOverviewTourState,OVERVIEW_TOUR_VERSION,PAGE_TOUR_EVENT,saveOverviewTourState} from './tour/overviewTour';
import { useLocation } from "react-router-dom";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Search,
  Star,
  Newspaper,
  BarChart3,
  Zap,
  Tag,
  Filter,
  Bell,
  TrendingUp,
  Building2,
  FlaskConical,
  Globe,
  Target,
  PieChart,
  Radar,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/* ─── Step definitions ─── */
const TOUR_STEPS = {
  dashboard: OVERVIEW_TOUR_STEPS,readOverviewTourState,

  /* Goal-specific flows (triggered by WelcomeOverlay) */
  research: [
    {id:"research-intro",title:"Find a company or asset",description:"Use a specific company name, ticker or asset name. Choose the right identity before opening its report.",icon:Search,target:null,position:"center"},
    {id:"research-omni",title:"Search for a subject",description:"The header search finds companies, assets and supported Markets topics. Research offers one dedicated company-and-asset search field.",icon:Search,target:"[data-testid='omni-search-input']",position:"bottom"},
    {id:"research-nav",title:"Refine in Research",description:"All, Companies and Assets narrow the matches without clearing your query. Similar names and spelling suggestions require your selection.",icon:Search,target:"[data-testid='research-mode-btn']",position:"bottom"},
    {id:"research-watchlist",title:"Check the evidence",description:"Coverage varies by company or asset. Check report sources, dates and gaps; an unavailable source is different from no saved match.",icon:Star,target:null,position:"center"},
    {id:"research-done",title:"Return to your research",description:"Recent searches appear below the Research search area for your signed-in account. Research help explains matching, corrections and coverage.",icon:Zap,target:null,position:"center"},
  ],

  landscape: [
    {id:"markets-intro",title:"Read the therapeutic landscape",description:"Define your therapeutic question, run fresh source collection, and inspect the resulting assets, studies and participants. Saved research keeps its original dates.",icon:Globe,target:null,position:"center"},
    {id:"markets-search",title:"Choose a defined scope",description:"Search an indication, area, class or mechanism. Choose the intended scope if a correction changes its meaning, then select Run fresh report.",icon:Search,target:"[data-tour='markets-search']",position:"bottom"},
    {id:"markets-evidence",title:"Compare assets and inspect evidence",description:"Source activity and retained results show what is happening. Check status restores the current run; Run again with fresh sources starts another. Expand assets to inspect studies and participant roles.",icon:FlaskConical,target:null,position:"center"},
    {id:"markets-airpack",title:"Take your evidence with you",description:"Review the actual AIRPack manifest and download the displayed version. Original records remain separate from normalized tables. Downloads do not refresh sources.",icon:Tag,target:null,position:"center"},
    {id:"markets-help",title:"Return to page help",description:"Use Help to review terms, source limits and this tour whenever you need a reminder.",icon:Search,target:"[data-tour='markets-help']",position:"bottom"},
  ],
  monitor: MONITOR_TOUR_STEPS,
  wire: MONITOR_TOUR_STEPS,

  watchlist: [
    { id: "wl-welcome", title: "Your Watchlist", description: "Track companies, assets, and news topics that matter most to your work.", icon: Star, target: null, position: "center" },
    { id: "wl-companies", title: "Track Companies", description: "Add verified companies to monitor their financial health, runway, and stock movements.", icon: Building2, target: "[data-testid='tab-companies']", position: "bottom" },
    { id: "wl-assets", title: "Track Assets", description: "Follow drugs and targets through their clinical development journey.", icon: FlaskConical, target: "[data-testid='tab-assets']", position: "bottom" },
  ],

  radar: [
    { id: "radar-welcome", title: "Biopharma Financing Disclosures", description: "Explore the latest saved SEC Form D filing per issuer. Offering and reported sold amounts are distinct; Form D does not establish private-company or stealth status.", icon: Radar, target: null, position: "center" },
    { id: "radar-stats", title: "Snapshot Summary", description: "Check issuer count, disclosed offering amount, sold as reported and verified trial matches. Totals describe the saved snapshot before shortlist filters.", icon: Activity, target: "[data-testid='private-market-radar-page']", position: "center" },
    { id: "radar-heatmap", title: "Explore Sectors", description: "Ranked bars is the default; Treemap is the alternate. Length or area follows the selected metric. Colors identify sectors. Zero and missing values for the selected metric have no tile but remain in the sector filter list.", icon: PieChart, target: "[data-testid='sector-heatmap-section']", position: "bottom" },
    { id: "radar-table", title: "Issuer Shortlist", description: "Open an issuer name for disclosed amounts, related persons and source-linked verified registrations. Select up to three checkboxes to compare evidence on this page.", icon: Building2, target: "[data-testid='private-market-radar-page']", position: "center" },
  ],
};

/* ─── Storage helpers ─── */
const key    = (page) => `bds_tour_completed_${page}`;
const dKey   = (page) => `bds_tour_dismissed_${page}`;
const LEGACY = "bds_onboarding_completed";

/* ─── Component ─── */
export default function OnboardingTour({page = 'dashboard',goalFlow = null,userId,orgId}) {
  const overview = page === 'dashboard';
  const flowKey = overview ? OVERVIEW_TOUR_VERSION : goalFlow || page;
  const steps = overview ? OVERVIEW_TOUR_STEPS : TOUR_STEPS[flowKey] || TOUR_STEPS[page] || [];
  const monitor = ['wire','monitor'].includes(flowKey);
  const markets = ['market-intel','markets','landscape'].includes(flowKey);
  const marketsStateKey = `bds_tour:${encodeURIComponent(userId || 'anonymous')}:${encodeURIComponent(orgId || 'personal')}:markets-fresh-v2`;
  const monitorStateKey = `bds_tour:${encodeURIComponent(userId || 'anonymous')}:${encodeURIComponent(orgId || 'personal')}:${MONITOR_TOUR_VERSION}`;
  const storageKey = markets ? `${marketsStateKey}:completed` : monitor ? `${monitorStateKey}:completed` : key(flowKey), dismissKey = markets ? `${marketsStateKey}:dismissed` : monitor ? `${monitorStateKey}:dismissed` : dKey(flowKey);
  const scope = `${userId || ''}:${orgId || ''}:${flowKey}`;
  const [activeScope,setActiveScope] = useState(null);
  const [step,setStep] = useState(0);
  const [target,setTarget] = useState(null);
  const [missing,setMissing] = useState(false);
  const tooltipRef = useRef(null);
  const returnFocus = useRef(null);
  const location = useLocation();
  const isActive = activeScope === scope;
  const current = steps[step];
  const geometry = useTourGeometry(target,tooltipRef,isActive);
  const start = useCallback(() => {
    returnFocus.current = document.activeElement;
    setStep(0);setMissing(false);setActiveScope(scope);
  },[scope]);

  // Overview is invitation/manual-only. Other page tours retain legacy eligibility.
  useEffect(() => {
    const forceKey = `bds_force_tour_${overview ? 'dashboard' : flowKey}`;
    let requested = false;
    try { requested = !!localStorage.getItem(forceKey); if(requested) localStorage.removeItem(forceKey); } catch { /* optional preference */ }
    if(requested){start();return;}
    if(overview || ((monitor || markets) && !userId)) return;
    let timer;
    try {
      if(localStorage.getItem('bds_welcome_seen') && !localStorage.getItem(storageKey) && !localStorage.getItem(dismissKey) && steps.length){timer=setTimeout(start,600);}
    } catch { /* No automatic tour when storage is unavailable. */ }
    return () => clearTimeout(timer);
  },[overview,monitor,markets,userId,flowKey,storageKey,dismissKey,steps.length,start]);

  useEffect(() => {
    const requested = event => {
      const tour = event.detail?.tourKey;
      if((overview && ['dashboard','overview',OVERVIEW_TOUR_VERSION].includes(tour)) || (!overview && tour === flowKey)) start();
    };
    window.addEventListener(PAGE_TOUR_EVENT,requested);
    return () => window.removeEventListener(PAGE_TOUR_EVENT,requested);
  },[overview,flowKey,start]);

  const routedRequest = useRef(null);
  const previousRoute = useRef(`${location.key || ''}:${location.pathname || ''}:${scope}`);
  useEffect(() => {
    const route = `${location.key || ''}:${location.pathname || ''}:${scope}`;
    const changed = previousRoute.current !== route;
    previousRoute.current = route;
    if(overview && location.state?.startOverviewTour && routedRequest.current !== location.key){routedRequest.current=location.key;start();}
    else if(changed) setActiveScope(null);
  },[location.key,location.pathname,overview,scope,start]);

  useLayoutEffect(() => {
    setTarget(null);setMissing(false);
    if(!isActive || !current?.target) return;
    let cancelled = false, observer, timeout;
    const find = () => {
      if(cancelled) return;
      const el = Array.from(document.querySelectorAll(current.target)).find(isVisibleTourTarget);
      if(!el) return;
      el.scrollIntoView?.({behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',block:'center',inline:'nearest'});
      setTarget(el);setMissing(false);observer?.disconnect();clearTimeout(timeout);
      return true;
    };
    if(find()) return;
    observer = new MutationObserver(find);
    observer.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['style','class','hidden']});
    timeout = setTimeout(() => {if(!find()){setMissing(true);observer.disconnect();}},2000);
    return () => {cancelled=true;clearTimeout(timeout);observer.disconnect();};
  },[isActive,current]);

  const finish = useCallback((completed=false) => {
    if(overview) saveOverviewTourState(userId,orgId,{[completed?'completed_at':'dismissed_at']:new Date().toISOString()});
    else {try {localStorage.setItem(completed?storageKey:dismissKey,'true');} catch { /* optional preference */ }}
    setActiveScope(null);
  },[overview,userId,orgId,storageKey,dismissKey]);

  // The overlay blocks background interaction, so keyboard focus stays in the dialog.
  useEffect(() => {
    if(!isActive) return;
    const dialog = tooltipRef.current;
    if(!dialog) return;
    const previous = returnFocus.current;
    const backgrounds = Array.from(document.body.children).filter(node => !node.contains(dialog) && !String(node.getAttribute('data-testid') || '').startsWith('tour-'));
    const states = backgrounds.map(node => [node,node.inert]);
    backgrounds.forEach(node => {node.inert=true;});
    dialog.querySelector('[data-testid="tour-next"]')?.focus();
    const onKey = event => {
      if(event.key === 'Escape'){event.preventDefault();event.stopPropagation();finish();return;}
      if(event.key !== 'Tab') return;
      const controls = Array.from(dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),[tabindex="0"]'));
      if(!controls.length){event.preventDefault();dialog.focus();return;}
      const first=controls[0],last=controls[controls.length-1];
      if(event.shiftKey && (document.activeElement===first || !dialog.contains(document.activeElement))){event.preventDefault();last.focus();}
      else if(!event.shiftKey && (document.activeElement===last || !dialog.contains(document.activeElement))){event.preventDefault();first.focus();}
    };
    document.addEventListener('keydown',onKey,true);
    return () => {document.removeEventListener('keydown',onKey,true);states.forEach(([node,inert])=>{node.inert=inert;});if(previous?.isConnected && !previous.inert) previous.focus?.();};
  },[isActive,finish]);

  if(!isActive || !current) return null;
  const Icon = current.icon;
  const isLast=step===steps.length-1,isFirst=step===0;
  const style=getPosition(current,geometry.rect,geometry.tooltip,geometry.viewport);
  return createPortal(<>
    <TourSpotlight rect={geometry.rect} viewport={geometry.viewport} onDismiss={()=>finish()}/>
    <div ref={tooltipRef} role="dialog" aria-modal="true" aria-labelledby="tour-step-title" aria-describedby="tour-step-description" tabIndex={-1}
      className="z-[10000] w-[360px] max-w-[calc(100vw-24px)] max-h-[calc(100dvh-24px)] bg-white border border-slate-200 rounded-sm shadow-sm overflow-y-auto" style={style} data-testid="tour-tooltip">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2"><Icon size={16} className="text-emerald-600"/><span role="status" className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Step {step+1} of {steps.length}</span></div>
        <button onClick={()=>finish()} aria-label="Dismiss tour" className="p-2 rounded-sm text-slate-500 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-emerald-600" data-testid="tour-dismiss"><X size={16}/></button>
      </div>
      <div className="px-5 py-4"><h3 id="tour-step-title" data-testid="tour-step-title" className="text-base font-semibold text-slate-900 mb-2">{current.title}</h3>
        <p id="tour-step-description" data-testid="tour-step-description" className="text-sm text-slate-600 leading-relaxed">{current.description}</p>
        {missing && <p role="status" className="text-xs text-slate-500 mt-2">This area is not visible in the current view. Continue to the next step or return to Help.</p>}
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
        <button onClick={()=>setStep(value=>Math.max(0,value-1))} disabled={isFirst} className="flex items-center gap-1 text-sm text-slate-600 disabled:text-slate-300 rounded-sm focus-visible:outline-2 focus-visible:outline-emerald-600" data-testid="tour-prev"><ChevronLeft size={14}/>Back</button>
        <Button onClick={()=>isLast?finish(true):setStep(value=>value+1)} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-sm" data-testid="tour-next">{isLast?'Finish tour':'Next'}{!isLast && <ChevronRight size={14} className="ml-1"/>}</Button>
      </div>
    </div>
  </>,document.body);
}

/* ─── Position calculator ─── */
function getPosition(step, rect, tooltip, viewport) {
  const tooltipW = tooltip.width;
  const tooltipH = tooltip.height;
  const vw = viewport?.width || window.innerWidth;
  const vh = viewport?.height || window.innerHeight;
  const pad = 12; // padding from viewport edges

  if (step.position === "center" || !rect) {
    return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const r = {...rect, right:rect.left+rect.width, bottom:rect.top+rect.height};
  const g = 16; // gap between target and tooltip
  let top, left;

  switch (step.position) {
    case "bottom":
      top = r.bottom + g;
      left = r.left + (r.width - tooltipW) / 2;
      // If tooltip would go below viewport, flip to top
      if (top + tooltipH > vh - pad) {
        top = r.top - g - tooltipH;
        if (top < pad) top = pad;
      }
      break;
    case "top":
      top = r.top - g - tooltipH;
      left = r.left + (r.width - tooltipW) / 2;
      // If tooltip would go above viewport, flip to bottom
      if (top < pad) {
        top = r.bottom + g;
      }
      break;
    case "right":
      top = r.top + r.height / 2 - tooltipH / 2;
      left = r.right + g;
      // If tooltip would go off right, flip to left
      if (left + tooltipW > vw - pad) {
        left = r.left - g - tooltipW;
      }
      break;
    case "left":
      top = r.top + r.height / 2 - tooltipH / 2;
      left = r.left - g - tooltipW;
      // If tooltip would go off left, flip to right
      if (left < pad) {
        left = r.right + g;
      }
      break;
    default:
      return {};
  }

  // Clamp top within viewport
  top = Math.max(pad, Math.min(top, vh - tooltipH - pad));
  // Clamp left within viewport
  left = Math.max(pad, Math.min(left, vw - tooltipW - pad));

  return { position: "fixed", top: `${top}px`, left: `${left}px` };
}

/** Overview replay never clears another module's history. */
export function restartOnboardingTour() {
  if(window.location.pathname === '/overview' || window.location.pathname === '/dashboard') {startPageTour('dashboard');return;}
  try {localStorage.setItem('bds_force_tour_dashboard','true');} catch { /* fallback navigation */ }
  window.location.assign('/overview');
}

export function startPageTour(tourKey) {
  window.dispatchEvent(new CustomEvent(PAGE_TOUR_EVENT,{detail:{tourKey}}));
}

export function useTourStatus(page = 'dashboard',userId,orgId) {
  if(page === 'dashboard') return {completed:!!readOverviewTourState(userId,orgId).completed_at};
  if(['market-intel','markets','landscape'].includes(page)){try{return {completed:!!localStorage.getItem(`bds_tour:${encodeURIComponent(userId || 'anonymous')}:${encodeURIComponent(orgId || 'personal')}:markets-fresh-v2:completed`)};}catch{return {completed:false};}}
  try {return {completed:!!(localStorage.getItem(key(page)) || localStorage.getItem(LEGACY))};} catch {return {completed:false};}
}
